import type Phaser from 'phaser';
import type { TileId } from '../../content/tiles';

/**
 * Props: the lawn's standing things in three-quarter view (docs/ART_DIRECTION.md, art study
 * 2026-10-03): each stands on its own tile and rises into the tile behind it, so a tree has a
 * trunk and a crown, a gnome a body and a tall hat. One frame per prop, 16 wide and 32 tall: the
 * bottom 16 rows are the prop's own tile. Lit from the top left, a soft ground shadow, and a crisp
 * dark outline added round each one. The lawn view draws the tile itself as calm ground
 * (`lawnBase`) and stands the prop on it; the flat tile frames stay for previews.
 */
export const PROPS_KEY = 'props';
export const PROP_W = 16;
export const PROP_H = 32;
/**
 * How far a prop stands up from its tile's bottom edge, so its footing and shadow sit just below
 * the middle of the tile (centered on it) rather than on the line below.
 */
export const PROP_LIFT = 4;

/** Frames in the sheet, left to right (append only: art sheets follow this layout). */
export const PROP_FRAMES = [
  'tree',
  'shrub',
  'rock',
  'gnome',
  'birdBath',
  'bench',
  'lampPost',
  'bin',
  'hayBale',
  'trough',
  'headstone',
  'lantern',
  'statue',
  'topiary',
  'deadTree',
  'cornerFlag',
  'ballWasher',
  'dogHouse',
  'urn',
  'cone',
  // Rolling Meadows.
  'beehive',
] as const;
export type PropFrame = (typeof PROP_FRAMES)[number];

const PROP_SET: ReadonlySet<string> = new Set(PROP_FRAMES);

/** The prop that stands on a tile of this art, if any. */
export function propFor(art: TileId): PropFrame | null {
  return PROP_SET.has(art) ? (art as PropFrame) : null;
}

export const propFrame = (p: PropFrame): number => PROP_FRAMES.indexOf(p);

// The art study's palette: bright, saturated, shadows leaning blue-green, highlights yellow.
const C = {
  s0: '#2a2c40',
  s1: '#555a75',
  s2: '#8a90a8',
  s3: '#bcc2d2',
  s4: '#f0f2f8',
  t0: '#3b230f',
  t1: '#6a3f1f',
  t2: '#9a6232',
  t3: '#c4874a',
  t4: '#e0ad6c',
  l0: '#123b1e',
  l1: '#1f5f2b',
  l2: '#2f8236',
  l3: '#4ea83f',
  l4: '#86cf52',
  l5: '#cdf07a',
  r0: '#6a1424',
  r1: '#c8303a',
  r2: '#ff5b47',
  r3: '#ffa684',
  b0: '#1f2f7a',
  b1: '#3a63d0',
  b2: '#6fa0ff',
  w1: '#2d82d4',
  w2: '#4ea9ef',
  w3: '#a2e0ff',
  k1: '#ffd2a8',
  wh: '#fffaf0',
  wh2: '#d5d8e4',
  o1: '#d65a14',
  o2: '#ff8c1f',
  o3: '#ffc463',
  y1: '#d99a0c',
  y2: '#ffd23f',
  y3: '#fff6b8',
  h1: '#b8902e',
  h2: '#e2bf55',
  h3: '#f6df8a',
  f1: '#ff6fa8',
  f2: '#ffe14a',
  f4: '#c084ff',
  m1: '#8a8f6a',
  m2: '#b5ba90',
} as const;
const INK = '#10180c';
/**
 * The lawn's own grass blades (placeholderArt's tufts), drawn round a prop's foot: painted in front
 * of it but never outlined, so the prop looks set into the lawn.
 */
const TUFT = { lit: '#d2f58a', mid: '#86d957', dark: '#2c7a2c' } as const;
const TUFT_COLORS: ReadonlySet<string> = new Set(Object.values(TUFT));
const SHADOW = 'rgba(10, 60, 24, 0.34)';

type Put = (x: number, y: number, color: string) => void;

/** Calls `fn` for every pixel inside an ellipse (frame pixels), with normalized offsets. */
function ell(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  fn: (x: number, y: number, d: number, ex: number, ey: number) => void,
): void {
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      const ex = (x + 0.5 - cx) / rx;
      const ey = (y + 0.5 - cy) / ry;
      const d = ex * ex + ey * ey;
      if (d <= 1) fn(x, y, d, ex, ey);
    }
  }
}

function rect(put: Put, x0: number, y0: number, w: number, h: number, color: string): void {
  for (let y = y0; y < y0 + h; y++) for (let x = x0; x < x0 + w; x++) put(x, y, color);
}

/** Small deterministic hash for texture. */
function hash(x: number, y: number, s: number): number {
  let h = Math.imul(x * 374761393 + y * 668265263 + s * 2147483647, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1103515245);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** A soft round shadow on the ground (not outlined). */
function shadow(put: Put, cx: number, cy: number, rx: number, ry: number): void {
  ell(cx, cy, rx, ry, (x, y) => put(x, y, SHADOW));
}

/** A leafy crown: overlapping lobes lit from the top left, dark beneath. */
function crown(put: Put, lobes: readonly [number, number, number, number][], under: number): void {
  const inside = (x: number, y: number) =>
    lobes.some(([cx, cy, rx, ry]) => {
      const ex = (x + 0.5 - cx) / rx;
      const ey = (y + 0.5 - cy) / ry;
      return ex * ex + ey * ey <= 1;
    });
  for (let y = 0; y < PROP_H; y++) {
    for (let x = 0; x < PROP_W; x++) {
      if (!inside(x, y)) continue;
      const lit = -((x - 8) / 8 + (y - under + 8) / 8);
      let c: string = lit > 0.6 ? C.l4 : lit > 0.05 ? C.l3 : lit > -0.6 ? C.l2 : C.l1;
      if (y > under) c = C.l1;
      if (lit > 0.85 && hash(x, y, 7) > 0.5) c = C.l5;
      put(x, y, c);
    }
  }
}

const PAINT: Record<PropFrame, (put: Put) => void> = {
  tree: (put) => {
    shadow(put, 8.5, 30, 7, 2.4);
    rect(put, 7, 23, 1, 8, C.t3);
    rect(put, 8, 23, 1, 8, C.t2);
    rect(put, 9, 23, 1, 8, C.t1);
    rect(put, 6, 30, 5, 1, C.t1);
    crown(
      put,
      [
        [8, 16.5, 7.2, 6.2],
        [3.8, 19.5, 4, 3.8],
        [12.2, 19.5, 4, 3.8],
        [8, 11.5, 5, 4.4],
      ],
      21,
    );
  },
  shrub: (put) => {
    shadow(put, 8.5, 29.5, 6.5, 2.2);
    ell(8, 24.5, 6.8, 5.6, (x, y, _d, ex, ey) => {
      const lit = -(ex + ey);
      put(x, y, lit > 0.75 ? C.l5 : lit > 0.25 ? C.l4 : lit > -0.35 ? C.l3 : C.l2);
    });
    for (const [x, y] of [
      [5, 22],
      [10, 21],
      [8, 26],
      [12, 25],
    ] as const)
      put(x, y, C.r2);
  },
  rock: (put) => {
    // A boulder sunk in the lawn: a lit top face, its front in shade, a dark right side, and the
    // grass growing up round its foot (so no outline shows where it meets the ground).
    shadow(put, 9, 29.5, 7.5, 1.6);
    const key: Record<string, string> = {
      '4': C.s4,
      '3': C.s3,
      '2': C.s2,
      '1': C.s1,
      '0': C.s0,
      G: TUFT.lit,
      g: TUFT.mid,
      d: TUFT.dark,
    };
    const art = [
      '.....4443.......',
      '...44444333.....',
      '..4444433333....',
      '.444443333332...',
      '.33333333222211.',
      '.32222222221111.',
      '.3222222221111..',
      '.G22222221111G..',
      'GgG2222111gG1gGg',
      'dgdGgdgGdgdGgdgd',
    ];
    const top = 31 - art.length;
    art.forEach((row, y) =>
      [...row].forEach((ch, x) => {
        if (ch !== '.') put(x, top + y, key[ch]!);
      }),
    );
  },

  gnome: (put) => {
    shadow(put, 8, 30.5, 5, 1.8);
    rect(put, 5, 29, 3, 2, C.t0);
    rect(put, 9, 29, 3, 2, C.t0);
    for (let y = 24; y < 29; y++) {
      for (let x = 4; x < 12; x++) put(x, y, x < 6 ? C.b2 : x > 9 ? C.b0 : C.b1);
    }
    rect(put, 4, 26, 8, 1, C.t1);
    put(7, 26, C.y2);
    ell(8, 24, 3.4, 3, (x, y, d) => put(x, y, d > 0.7 ? C.wh2 : C.wh));
    rect(put, 6, 21, 4, 2, C.k1);
    put(7, 22, C.r2);
    put(8, 22, C.r2);
    put(6, 21, INK);
    put(9, 21, INK);
    // The hat: a tall cone, leaning back, lit left.
    for (let y = 12; y < 21; y++) {
      const f = (y - 12) / 9;
      const half = 0.6 + f * 3.6;
      const cx = 8.6 - f * 0.8;
      for (let x = Math.floor(cx - half); x < cx + half; x++) {
        put(x, y, x < cx - half * 0.3 ? C.r3 : x > cx + half * 0.5 ? C.r0 : C.r2);
      }
    }
  },
  birdBath: (put) => {
    shadow(put, 8.5, 30.5, 5, 1.6);
    rect(put, 5, 29, 7, 2, C.s1);
    rect(put, 7, 22, 1, 7, C.s3);
    rect(put, 8, 22, 2, 7, C.s2);
    ell(8.5, 20, 6.5, 2.8, (x, y, _d, _ex, ey) => put(x, y, ey > 0.3 ? C.s1 : C.s3));
    ell(8.5, 19.6, 5, 1.7, (x, y, _d, ex) => put(x, y, ex < -0.3 ? C.w3 : C.w2));
  },
  bench: (put) => {
    shadow(put, 8.5, 30, 8, 1.8);
    for (const x of [2, 13]) rect(put, x, 24, 1, 6, C.s0);
    for (let y = 17; y < 21; y++) for (let x = 1; x < 15; x++) put(x, y, y % 2 ? C.t2 : C.t3);
    for (let y = 22; y < 26; y++)
      for (let x = 1; x < 15; x++) put(x, y, y === 22 ? C.t4 : y % 2 ? C.t2 : C.t3);
    for (const x of [2, 13]) rect(put, x, 17, 1, 9, C.s0);
  },
  lampPost: (put) => {
    shadow(put, 8.5, 30.5, 3.5, 1.4);
    rect(put, 5, 29, 6, 2, C.s0);
    rect(put, 7, 8, 1, 21, C.s1);
    rect(put, 8, 8, 1, 21, C.s0);
    rect(put, 5, 2, 6, 1, C.s0);
    rect(put, 4, 3, 8, 1, C.s0);
    rect(put, 5, 4, 6, 4, C.y2);
    rect(put, 5, 4, 2, 2, C.y3);
    rect(put, 5, 8, 6, 1, C.s0);
  },
  // A blue wheelie bin with a yellow lid (owner, 2026-10-28: the gray one read as a headstone).
  bin: (put) => {
    shadow(put, 8.5, 30.5, 5.5, 1.6);
    // The body, a little narrower at the foot, lit from the left, two molded grooves down it.
    for (let y = 19; y < 30; y++) {
      const inset = y >= 27 ? 1 : 0;
      for (let x = 3 + inset; x < 13 - inset; x++) {
        const edge = x === 3 + inset ? '#6f9bf0' : x >= 11 - inset ? '#263f8f' : '#3a63d0';
        put(x, y, edge);
      }
      if (y > 20 && y < 28) for (const x of [6, 9]) put(x, y, '#2c4fb0');
    }
    // The handle across the top of the front.
    rect(put, 5, 20, 6, 1, '#1d2f6e');
    // The yellow lid, overhanging the body, its front edge in shade.
    for (let x = 2; x < 14; x++) {
      put(x, 16, x < 5 ? '#fff3a8' : '#ffe066');
      put(x, 17, '#ffd23f');
      put(x, 18, '#c99a17');
    }
    rect(put, 6, 15, 4, 1, '#ffd23f');
    // Two wheels at the foot.
    for (const x of [3, 11]) {
      rect(put, x, 29, 2, 2, '#222326');
      put(x, 29, '#8f969d');
    }
  },
  hayBale: (put) => {
    shadow(put, 8.5, 30.5, 7.5, 1.8);
    for (let y = 21; y < 30; y++) {
      for (let x = 1; x < 15; x++) {
        const c = y < 23 ? C.h3 : hash(x, y, 4) > 0.8 ? C.h1 : C.h2;
        put(x, y, c);
      }
    }
    for (const x of [5, 10]) rect(put, x, 21, 1, 9, C.t1);
  },
  trough: (put) => {
    shadow(put, 8.5, 30.5, 7.5, 1.8);
    for (let y = 23; y < 30; y++) for (let x = 1; x < 15; x++) put(x, y, x < 3 ? C.s3 : C.s2);
    for (let x = 1; x < 15; x++) put(x, 23, C.s4);
    rect(put, 2, 24, 12, 2, C.w2);
    rect(put, 3, 24, 4, 1, C.w3);
    for (const x of [2, 13]) rect(put, x, 29, 1, 2, C.s0);
  },
  headstone: (put) => {
    shadow(put, 9, 30.5, 6, 1.6);
    for (let y = 17; y < 30; y++) {
      for (let x = 4; x < 12; x++) {
        const cx = x - 7.5;
        if (y < 20 && cx * cx + (y - 20) * (y - 20) * 1.6 > 14) continue;
        put(x, y, x <= 5 ? C.s4 : x >= 10 ? C.s2 : C.s3);
      }
    }
    rect(put, 6, 22, 4, 1, C.s1);
    rect(put, 6, 24, 4, 1, C.s1);
  },
  lantern: (put) => {
    shadow(put, 8.5, 30.5, 3.5, 1.4);
    rect(put, 6, 29, 4, 2, C.s0);
    rect(put, 7, 18, 1, 11, C.s1);
    rect(put, 8, 18, 1, 11, C.s0);
    rect(put, 6, 9, 4, 1, C.s0);
    rect(put, 5, 10, 6, 1, C.s0);
    rect(put, 5, 11, 1, 6, C.s0);
    rect(put, 10, 11, 1, 6, C.s0);
    rect(put, 6, 11, 4, 6, C.y2);
    rect(put, 6, 11, 2, 2, C.y3);
    rect(put, 5, 17, 6, 1, C.s0);
  },
  statue: (put) => {
    shadow(put, 8.5, 30.5, 7, 1.8);
    for (let y = 25; y < 30; y++)
      for (let x = 2; x < 14; x++) put(x, y, y === 25 ? C.s4 : x < 4 ? C.s3 : C.s2);
    // A figure on its plinth: robe, arm, head, all marble lit from the left.
    for (let y = 14; y < 25; y++) {
      const half = 2 + (y - 14) * 0.18;
      for (let x = Math.floor(8 - half); x < 8 + half; x++) put(x, y, x < 7 ? C.wh : C.wh2);
    }
    rect(put, 10, 16, 2, 4, C.wh2);
    ell(8, 12, 2.2, 2.2, (x, y, _d, ex) => put(x, y, ex < 0 ? C.wh : C.wh2));
  },
  topiary: (put) => {
    shadow(put, 8.5, 30.5, 5, 1.6);
    rect(put, 5, 27, 7, 4, C.r1);
    rect(put, 5, 27, 7, 1, C.r3);
    rect(put, 8, 22, 1, 5, C.t2);
    ell(8, 18, 6, 5.6, (x, y, _d, ex, ey) => {
      const lit = -(ex + ey);
      put(x, y, lit > 0.75 ? C.l5 : lit > 0.2 ? C.l4 : lit > -0.4 ? C.l3 : C.l2);
    });
  },
  deadTree: (put) => {
    // A gnarled snag in weathered gray: the trunk lit on its left, a hollow knot, roots gripping
    // the lawn, two crooked limbs forking into bare, broken-off branches.
    shadow(put, 9, 30.5, 6.5, 1.8);
    const wood = ['#d6c6aa', '#ad9a82', '#857260', '#5e4f42', '#3a3029'];
    const art = [
      '..1..........1..',
      '..12........12..',
      '...2....1...2...',
      '...12...12.12...',
      '.1..2....122....',
      '.12.12...22.....',
      '..1212..122.....',
      '....1221223.....',
      '.....122223.....',
      '......12223.....',
      '......01223.....',
      '......01223.....',
      '......01123.....',
      '......01143.....',
      '......01443.....',
      '......01223.....',
      '......01223.....',
      '......012233....',
      '......01223.....',
      '......01223.....',
      '.....001223.....',
      '....0011223.3...',
      '...001.1223333..',
      '..00...12.3..3..',
    ];
    const top = 31 - art.length;
    art.forEach((row, y) =>
      [...row].forEach((ch, x) => {
        if (ch !== '.') put(x, top + y, wood[Number(ch)]!);
      }),
    );
  },

  cornerFlag: (put) => {
    shadow(put, 8, 30.5, 3, 1.2);
    rect(put, 7, 12, 1, 19, C.wh);
    for (let y = 12; y < 18; y++) {
      for (let x = 8; x < 8 + (18 - y) && x < 15; x++) put(x, y, y < 15 ? C.o2 : C.y2);
    }
  },
  ballWasher: (put) => {
    shadow(put, 8.5, 30.5, 4, 1.4);
    rect(put, 7, 20, 2, 10, C.s1);
    rect(put, 4, 15, 8, 6, C.b1);
    rect(put, 4, 15, 8, 1, C.b2);
    rect(put, 5, 16, 2, 2, C.b2);
    rect(put, 11, 17, 2, 1, C.s2);
  },
  dogHouse: (put) => {
    shadow(put, 8.5, 30.5, 7.5, 1.8);
    for (let y = 21; y < 31; y++) for (let x = 2; x < 14; x++) put(x, y, x < 4 ? C.t4 : C.t3);
    for (let y = 14; y < 22; y++) {
      const half = (y - 14) * 0.95;
      for (let x = Math.floor(8 - half - 1); x < 8 + half + 1; x++) put(x, y, x < 8 ? C.r2 : C.r1);
    }
    ell(8, 28, 2.6, 3.2, (x, y) => put(x, y, '#2a1c12'));
  },
  urn: (put) => {
    shadow(put, 8.5, 30.5, 5, 1.6);
    rect(put, 6, 28, 5, 2, C.s1);
    ell(8.5, 24, 5, 4.4, (x, y, _d, ex) => put(x, y, ex < -0.3 ? C.s4 : ex > 0.4 ? C.s2 : C.s3));
    for (const [x, y, c] of [
      [5, 19, C.f1],
      [7, 18, C.f2],
      [10, 19, C.f4],
      [9, 17, C.f1],
      [6, 20, C.l3],
      [11, 20, C.l3],
      [8, 20, C.l4],
    ] as const) {
      put(x, y, c);
      put(x + 1, y, c);
    }
  },
  cone: (put) => {
    shadow(put, 8.5, 30.5, 5, 1.5);
    rect(put, 3, 28, 10, 2, C.o1);
    for (let y = 17; y < 28; y++) {
      const half = 0.7 + (y - 17) * 0.38;
      for (let x = Math.floor(8 - half); x < 8 + half; x++) {
        const band = y >= 21 && y <= 23;
        put(x, y, band ? (x < 8 ? C.wh : C.wh2) : x < 7 ? C.o3 : C.o2);
      }
    }
  },
  beehive: (put) => {
    // A straw skep on a little wooden stand: a dome of coiled bands lit from the left, its dark
    // doorway at the foot, a few bees about it.
    shadow(put, 8.5, 30.5, 5.5, 1.6);
    rect(put, 5, 26, 1, 5, C.t1);
    rect(put, 11, 26, 1, 5, C.t1);
    rect(put, 3, 25, 11, 2, C.t3);
    rect(put, 3, 26, 11, 1, C.t2);
    ell(8.5, 20.5, 5.4, 5, (x, y, _d, ex) => {
      const band = y % 2 === 0;
      put(
        x,
        y,
        ex < -0.35 ? (band ? C.h3 : C.y3) : ex > 0.5 ? (band ? C.h1 : C.y1) : band ? C.h2 : C.h3,
      );
    });
    rect(put, 7, 23, 3, 2, '#3a2a14');
    for (const [x, y] of [
      [2, 15],
      [14, 13],
      [13, 18],
    ] as const) {
      put(x, y, C.y2);
      put(x + 1, y, '#10180c');
    }
  },
};

/**
 * The lawn grows up round a prop's foot: a ragged fringe of grass blades (one to three pixels
 * tall, lit at their tips) over its lowest row, reaching a pixel past it on each side, so the prop
 * stands in the turf rather than on it (and no outline shows where it meets the ground).
 */
function growGrass(solid: Uint8Array, tuft: (string | undefined)[]): void {
  let foot = -1;
  for (let i = 0; i < solid.length; i++) if (solid[i]) foot = Math.floor(i / PROP_W);
  if (foot < 0) return;
  const near = (x: number) =>
    x >= 0 &&
    x < PROP_W &&
    (solid[foot * PROP_W + x] === 1 || solid[(foot - 1) * PROP_W + x] === 1);
  for (let x = 0; x < PROP_W; x++) {
    const under = near(x);
    if (!under && !near(x - 1) && !near(x + 1)) continue;
    const h = under ? 1 + Math.floor(hash(x, foot, 11) * 3) : 1 + Math.floor(hash(x, foot, 12) * 2);
    for (let k = 0; k < h; k++) {
      const i = (foot - k) * PROP_W + x;
      solid[i] = 0;
      tuft[i] =
        k === h - 1 && k > 0
          ? hash(x, k, 13) > 0.45
            ? TUFT.lit
            : TUFT.mid
          : k === 0
            ? TUFT.dark
            : TUFT.mid;
    }
  }
}

/** Paints one prop (with its outline) into a frame at x offset `ox` on a canvas context. */
export function paintProp(ctx: CanvasRenderingContext2D, ox: number, prop: PropFrame): void {
  const solid = new Uint8Array(PROP_W * PROP_H);
  const colors = new Array<string>(PROP_W * PROP_H);
  const shade = new Uint8Array(PROP_W * PROP_H);
  const tuft = new Array<string | undefined>(PROP_W * PROP_H);
  PAINT[prop]((x, y, color) => {
    if (x < 0 || y < 0 || x >= PROP_W || y >= PROP_H) return;
    const i = y * PROP_W + x;
    if (color === SHADOW) {
      if (!solid[i]) shade[i] = 1;
      return;
    }
    if (TUFT_COLORS.has(color)) {
      if (!solid[i]) tuft[i] = color;
      return;
    }
    solid[i] = 1;
    shade[i] = 0;
    tuft[i] = undefined;
    colors[i] = color;
  });
  // The rock paints its own grass; the topiary stands in a pot, on the lawn.
  if (prop !== 'rock' && prop !== 'topiary') growGrass(solid, tuft);
  const at = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < PROP_W && y < PROP_H && solid[y * PROP_W + x] === 1;
  for (let y = 0; y < PROP_H; y++) {
    for (let x = 0; x < PROP_W; x++) {
      const i = y * PROP_W + x;
      if (solid[i]) {
        ctx.fillStyle = colors[i]!;
      } else if (tuft[i]) {
        ctx.fillStyle = tuft[i];
      } else if (at(x - 1, y) || at(x + 1, y) || at(x, y - 1) || at(x, y + 1)) {
        ctx.fillStyle = INK;
      } else if (shade[i]) {
        ctx.fillStyle = SHADOW;
      } else continue;
      ctx.fillRect(ox + x, y, 1, 1);
    }
  }
}

export function createProps(scene: Phaser.Scene): void {
  const tex = scene.textures.createCanvas(PROPS_KEY, PROP_W * PROP_FRAMES.length, PROP_H);
  if (!tex) throw new Error('Could not create props texture');
  PROP_FRAMES.forEach((p, i) => paintProp(tex.context, i * PROP_W, p));
  tex.refresh();
  for (let i = 0; i < PROP_FRAMES.length; i++) tex.add(i, 0, i * PROP_W, 0, PROP_W, PROP_H);
}
